import {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationLink,
} from '@/components/ui/pagination';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronLeftSquare, ChevronRight } from 'lucide-react';
import { pageSizeOptions } from '@/constants/paginationOptions';

interface TablePaginationProps {
	table: any;
	isFetching: boolean;
}

export function TablePagination({ table, isFetching }: TablePaginationProps) {
	const pagination = table.getState().pagination;

	const pageCount = table.getPageCount();
	const currentPage = pagination.pageIndex;

	const visiblePages = Array.from({ length: pageCount })
		.slice(Math.max(0, currentPage - 2), Math.min(pageCount, currentPage + 3))
		.map((_, i) => Math.max(0, currentPage - 2) + i);

	return (
		<div className="mt-4 flex justify-between">
			<Pagination className="w-fit">
				<PaginationContent className="w-fit">
					<div className="flex items-center gap-4 space-x-2">
						{/* First */}
						<PaginationItem>
							<PaginationLink
								onClick={
									!isFetching && table.getCanPreviousPage()
										? () => table.setPageIndex(0)
										: undefined
								}
								className={
									isFetching || !table.getCanPreviousPage()
										? 'pointer-events-none cursor-default opacity-50 hover:bg-transparent hover:text-inherit'
										: 'cursor-pointer hover:bg-gray-100'
								}
								aria-disabled={isFetching || !table.getCanPreviousPage()}
							>
								<ChevronFirst size={16} strokeWidth={2} />
								<span className="mr-1 font-normal"></span>
							</PaginationLink>
						</PaginationItem>

						{/* Previous */}
						<PaginationItem>
							<PaginationLink
								onClick={
									!isFetching && table.getCanPreviousPage()
										? () => table.previousPage()
										: undefined
								}
								className={
									isFetching || !table.getCanPreviousPage()
										? 'pointer-events-none cursor-default opacity-50 hover:bg-transparent hover:text-inherit'
										: 'cursor-pointer hover:bg-gray-100'
								}
								aria-disabled={isFetching || !table.getCanPreviousPage()}
							>
								<ChevronLeft size={16} strokeWidth={2} />
								<span className="mr-1 font-normal"></span>
							</PaginationLink>
						</PaginationItem>

						{/* Page Numbers */}
						{visiblePages.map((pageNumber) => (
							<PaginationItem key={pageNumber}>
								<PaginationLink
									onClick={
										!isFetching ? () => table.setPageIndex(pageNumber) : undefined
									}
									aria-disabled={isFetching}
									className={`px-3 py-1 ${
										table.getState().pagination.pageIndex === pageNumber
											? 'rounded-md border border-gray-300 bg-white font-semibold text-black shadow-sm'
											: 'text-gray-500'
									}`}
								>
									{pageNumber + 1}
								</PaginationLink>
							</PaginationItem>
						))}

						{/* Next */}
						<PaginationItem className="ml-8">
							<PaginationLink
								onClick={
									!isFetching && table.getCanNextPage()
										? () => table.nextPage()
										: undefined
								}
								className={
									isFetching || !table.getCanNextPage()
										? 'pointer-events-none cursor-default opacity-50 hover:bg-transparent hover:text-inherit'
										: 'cursor-pointer hover:bg-gray-100'
								}
								aria-disabled={isFetching || !table.getCanNextPage()}
							>
								<span className="font-normal"></span>
								<ChevronRight size={16} strokeWidth={2} />
							</PaginationLink>
						</PaginationItem>

						{/* Last */}
						<PaginationItem>
							<PaginationLink
								onClick={
									!isFetching && table.getCanNextPage()
										? () => table.setPageIndex(pageCount - 1)
										: undefined
								}
								className={
									isFetching || !table.getCanNextPage()
										? 'pointer-events-none cursor-default opacity-50 hover:bg-transparent hover:text-inherit'
										: 'cursor-pointer hover:bg-gray-100'
								}
								aria-disabled={isFetching || !table.getCanNextPage()}
							>
								<span className="font-normal"></span>
								<ChevronLast size={16} strokeWidth={2} />
							</PaginationLink>
						</PaginationItem>
					</div>
				</PaginationContent>
			</Pagination>

			{/* Page Size Selector */}
			<div className="flex items-center justify-center">
				<Select
					value={pagination.pageSize.toString()}
					aria-label="Results per page"
					onValueChange={(e) => table.setPageSize(Number(e))}
				>
					<SelectTrigger id="results-per-page" className="w-fit whitespace-nowrap">
						<SelectValue placeholder="Select number of results" />
					</SelectTrigger>
					<SelectContent>
						{pageSizeOptions.map((size) => (
							<SelectItem key={size} value={size.toString()}>
								{size}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Label className="ml-2">Results per page</Label>
			</div>
		</div>
	);
}